import { Search } from "lucide-react";
import { useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import { cn } from "../../utils";
import { Input } from "./input";
import { Loader } from "./loader";

export type SearchSelectorProps<T> = {
  items?: T[] | undefined;
  selectedId: string | null;
  onSelect: (item: T) => void;
  getItemId: (item: T) => string;
  getSearchString: (item: T) => string;
  renderItem: (item: T, isSelected: boolean) => ReactNode;
  placeholder: string;
  noResultsText: string;
  isLoading?: boolean;
};

export default function SearchSelector<T>({
  items,
  selectedId,
  onSelect,
  getItemId,
  getSearchString,
  renderItem,
  placeholder,
  noResultsText,
  isLoading,
}: SearchSelectorProps<T>) {
  const [query, setQuery] = useState("");

  const filteredItems = useMemo(() => {
    if (!items) {
      return [] as T[];
    }

    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return items;
    }

    return items.filter((item) =>
      getSearchString(item).toLowerCase().includes(normalizedQuery),
    );
  }, [getSearchString, items, query]);

  return (
    <div>
      <div className="mb-3">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-content-muted">
            <Search className="h-4 w-4" />
          </div>
          <Input
            placeholder={placeholder}
            value={query}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setQuery(event.target.value)
            }
            className="rounded-xl border border-border-subtle px-3 py-2 pl-10"
          />
        </div>
      </div>

      <div className="max-h-[60dvh] space-y-2 overflow-y-auto pr-2">
        {isLoading ? (
          <div className="p-4">
            <Loader />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-sm text-content-muted">{noResultsText}</div>
        ) : (
          filteredItems.map((item) => {
            const itemId = getItemId(item);
            const isSelected = selectedId === itemId;

            return (
              <button
                key={itemId}
                type="button"
                onClick={() => onSelect(item)}
                className={cn(
                  "group relative w-full overflow-hidden rounded-lg px-3 py-2.5 text-left transition-colors duration-200",
                  isSelected
                    ? "bg-primary/10 font-medium text-content-accent before:absolute before:bottom-0 before:left-0 before:top-0 before:w-[3px] before:bg-primary"
                    : "text-content-secondary hover:bg-surface-secondary/70 hover:text-content-accent",
                )}
              >
                <div className="flex w-full items-start gap-3">
                  {renderItem(item, isSelected)}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
